import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Box,
  Divider,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';

const faqData = [
  {
    topic: 'Cleaning Details',
    icon: <CleaningServicesIcon color="primary" />,
    items: [
      {
        question: "What's included in a cleaning? What's not included?",
        answer: `Our fairies perform a thorough standard cleaning, including dusting, vacuuming, mopping, and sanitizing kitchens and bathrooms. However, we don’t move heavy items, clean carpets, handle mold or pest infestations, treat deep stains, clean pet messes, or do any work outside or above normal reach (like blinds).`,
      },
      {
        question: 'Do cleaners bring their own supplies?',
        answer: `Yes, our fairies bring their own cleaning products and equipment. If you prefer they use yours, just let them know ahead of time.`,
      },
    ],
  },
  {
    topic: 'Pricing & Membership',
    icon: <MonetizationOnIcon color="primary" />,
    items: [
      {
        question: 'How much is the first cleaning? And why is it so affordable?',
        answer: `As a new member of 5 Fairies Cleaning, your first cleaning is deeply discounted: $9 for 2 hours, $19 for 3 hours, $39 for 4 hours, or $79 for 6 hours. It’s our way of welcoming you! Cleaners still receive their full pay — we cover the difference because we're confident you'll love the experience.`,
      },
      {
        question: 'What does it cost after the first cleaning?',
        answer: `After your first cleaning, regular bookings average $22/hour for members — that’s about 50% off our one-time rates. Prices may vary slightly depending on your area and selected fairy, and we always show the total cost before you book.`,
      },
      {
        question: 'Why pay the $49 membership fee?',
        answer: `Our membership unlocks exclusive discounted rates — just like Costco or Amazon Prime. The $49 fee pays for itself with your first cleaning and helps us maintain our high-quality platform while ensuring our fairies receive 100% of their pay.`,
      },
    ],
  },
  {
    topic: 'Membership Terms',
    icon: <SettingsIcon color="primary" />,
    items: [
      {
        question: 'When does membership start? Can I cancel?',
        answer: `Membership only begins after your first cleaning. There’s a 6-month commitment period. Cancel anytime after that with no penalty. If you cancel before 6 months, your first cleaning will be retroactively charged at the regular rate.`,
      },
      {
        question: 'How do I cancel my membership?',
        answer: `To cancel your membership, log into your dashboard, go to Settings > Membership, and click "Cancel." Early cancellations (within 6 months) result in your initial discount being reversed. You can pause or reactivate your membership at any time.`,
      },
    ],
  },
  {
    topic: 'Booking & Scheduling',
    icon: <ScheduleIcon color="primary" />,
    items: [
      {
        question: 'How long should I book? How often?',
        answer: `For a 1 bed/1 bath home, book 3 hours. For 2 bed/2 bath homes, 4 hours. For larger homes, we recommend 6+ hours. Most clients book cleanings once or twice a month. If you have pets or a big household, weekly cleanings are ideal.`,
      },
      {
        question: 'How far in advance should I book? Can I reschedule?',
        answer: `We recommend booking 3 days in advance. Need something sooner? Next-day and same-day cleanings are available for a small fee. You can reschedule up to 6 hours before your cleaning without penalty.`,
      },
    ],
  },
  {
    topic: 'Cleaners',
    icon: <PersonIcon color="primary" />,
    items: [
      {
        question: 'How much do cleaners get paid?',
        answer: `All 5 Fairies cleaners set their own rates and keep 100% of their earnings, including any tips. Even during your discounted first cleaning, our fairies receive their full rate — we cover the difference.`,
      },
      {
        question: 'Who will clean my home? Are they qualified?',
        answer: `You can view your assigned cleaner’s profile, reviews, and history before the appointment. Don’t love the match? Choose a different fairy! All of our cleaners are background-checked and maintain an average 4.8-star rating.`,
      },
    ],
  },
  {
    topic: 'Extra Services',
    icon: <LocalLaundryServiceIcon color="primary" />,
    items: [
      {
        question: 'Do you offer other services besides cleaning?',
        answer: `Yes! We also offer laundry folding, oven cleaning, fridge and cabinet deep cleaning, and more. Just let us know what you need when booking — our fairies are happy to help!`,
      },
    ],
  },
];

const FAQ = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Frequently Asked Questions
      </Typography>
      {faqData.map((section, index) => (
        <Box key={index} sx={{ mt: 5 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            {section.icon}
            <Typography variant="h6" fontWeight="bold">
              {section.topic}
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          {section.items.map((item, idx) => (
            <Accordion key={idx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="text.secondary">
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </Container>
  );
};

export default FAQ;
